import { useEffect, useState } from "react";
import { Avatar } from "../components/ui/Avatar";
import { useParams, useNavigate, Link } from "react-router-dom";
import { AppShell } from "../components/layout/AppShell";
import { Card, Badge, Skeleton, ErrorState } from "../components/ui/Primitives";
import { Button } from "../components/ui/Button";
import { Modal } from "../components/ui/Modal";
import { StepUpDialog } from "../components/ui/StepUpDialog";
import { Input, Select, Textarea } from "../components/ui/Fields";
import { apiRequest, ApiError } from "../api/client";
import { useToast } from "../components/ui/Toast";
import type { UserBan, UserDetail } from "../api/types";

type Duration = "1" | "7" | "permanent" | "custom";

const reasonTone: Record<string, "success" | "danger" | "neutral" | "gold"> = {
  iap_purchase: "success",
  rewarded_ad: "success",
  gift_received: "success",
  refund: "success",
  gift_sent: "danger",
  withdrawal: "danger",
  vip_purchase: "gold",
};

function formatBanRow(ban: UserBan): string {
  const parts = [ban.ban_type === "device" ? "Device ban" : "Account ban"];
  if (ban.reason) parts.push(`— "${ban.reason}"`);
  return parts.join(" ");
}

export function UserDetailPage() {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const [detail, setDetail] = useState<UserDetail | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { showToast } = useToast();

  // Round 33 points 13/14 — Ban / Unban with a real form (type, duration,
  // reason) instead of the old plain boolean toggle, still through
  // StepUpDialog exactly as before (routes/admin/users.js's setBanned/
  // unbanUser both call requireStepUp).
  const [isBanFormOpen, setIsBanFormOpen] = useState(false);
  const [banType, setBanType] = useState<"account" | "device">("account");
  const [duration, setDuration] = useState<Duration>("permanent");
  const [customDays, setCustomDays] = useState("");
  const [banReason, setBanReason] = useState("");
  const [deviceFingerprint, setDeviceFingerprint] = useState("");
  const [pendingStepUp, setPendingStepUp] = useState<"ban" | "unban" | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [banHistory, setBanHistory] = useState<UserBan[] | null>(null);
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);

  // Round 33 points 15/16 — admin edit (username/avatar/gender/country/signature).
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [editForm, setEditForm] = useState({ username: "", avatarUrl: "", gender: "", country: "", signature: "", usernameColor: "", adminBadgeIconUrl: "" });
  const [isSavingEdit, setIsSavingEdit] = useState(false);

  function load() {
    if (!userId) return;
    setIsLoading(true);
    setError(null);
    apiRequest<UserDetail>(`/admin/users/${userId}/detail`)
      .then(setDetail)
      .catch((err) => setError(err instanceof ApiError ? err.message : "could not load user"))
      .finally(() => setIsLoading(false));
  }

  useEffect(load, [userId]);

  async function toggleVerify() {
    if (!detail) return;
    try {
      await apiRequest(`/admin/users/${userId}/verify`, { method: "POST", body: { verified: !detail.profile.is_verified } });
      showToast("Verification updated", "success");
      load();
    } catch (err) {
      showToast(err instanceof ApiError ? err.message : "Could not update", "error");
    }
  }

  function openBanForm() {
    setBanType("account");
    setDuration("permanent");
    setCustomDays("");
    setBanReason("");
    setDeviceFingerprint("");
    setIsBanFormOpen(true);
  }

  function submitBanForm() {
    if (banType === "device" && !deviceFingerprint.trim()) {
      showToast("A device fingerprint is required for a device ban", "error");
      return;
    }
    if (duration === "custom" && (!customDays.trim() || Number(customDays) <= 0)) {
      showToast("Enter a valid number of days", "error");
      return;
    }
    setIsBanFormOpen(false);
    setPendingStepUp("ban");
  }

  function durationDaysValue(): number | null {
    if (duration === "permanent") return null;
    if (duration === "custom") return Number(customDays);
    return Number(duration);
  }

  async function runStepUpAction(stepUpToken: string) {
    if (!pendingStepUp || !userId) return;
    setIsSubmitting(true);
    try {
      if (pendingStepUp === "ban") {
        await apiRequest(`/admin/users/${userId}/ban`, {
          method: "POST",
          body: {
            banned: true,
            banType,
            reason: banReason.trim() || undefined,
            durationDays: durationDaysValue(),
            deviceFingerprint: banType === "device" ? deviceFingerprint.trim() : undefined,
            stepUpToken,
          },
        });
        showToast("User banned", "success");
      } else {
        await apiRequest(`/admin/users/${userId}/unban`, { method: "POST", body: { stepUpToken } });
        showToast("User unbanned", "success");
      }
      load();
      if (isHistoryOpen) loadHistory();
    } catch (err) {
      showToast(err instanceof ApiError ? err.message : "Could not update ban status", "error");
    } finally {
      setIsSubmitting(false);
      setPendingStepUp(null);
    }
  }

  function loadHistory() {
    if (!userId) return;
    setIsLoadingHistory(true);
    apiRequest<UserBan[]>(`/admin/users/${userId}/bans`)
      .then(setBanHistory)
      .catch((err) => showToast(err instanceof ApiError ? err.message : "Could not load ban history", "error"))
      .finally(() => setIsLoadingHistory(false));
  }

  function openHistory() {
    setIsHistoryOpen(true);
    loadHistory();
  }

  function openEditForm() {
    if (!detail) return;
    setEditForm({
      username: detail.profile.username,
      avatarUrl: detail.profile.avatar_url ?? "",
      gender: "",
      country: detail.profile.country ?? "",
      signature: "",
      usernameColor: detail.profile.username_color_hex ?? "",
      adminBadgeIconUrl: detail.profile.admin_badge_icon_url ?? "",
    });
    setIsEditOpen(true);
  }

  async function saveEdit() {
    if (!userId) return;
    setIsSavingEdit(true);
    try {
      await apiRequest(`/admin/users/${userId}`, {
        method: "PATCH",
        body: {
          username: editForm.username.trim() || undefined,
          avatarUrl: editForm.avatarUrl.trim() || undefined,
          gender: editForm.gender || undefined,
          country: editForm.country.trim() || undefined,
          signature: editForm.signature,
          // Round 42 item 1 — empty picker value means "clear to default"
          // (explicit null), never omitted/left-as-is, so unsetting a
          // previously-set color actually works from this form.
          usernameColor: editForm.usernameColor.trim() || null,
          // Round 43 — same explicit-null-to-clear convention as usernameColor.
          adminBadgeIconUrl: editForm.adminBadgeIconUrl.trim() || null,
        },
      });
      showToast("User updated", "success");
      setIsEditOpen(false);
      load();
    } catch (err) {
      showToast(err instanceof ApiError ? err.message : "Could not update user", "error");
    } finally {
      setIsSavingEdit(false);
    }
  }

  if (isLoading) {
    return (
      <AppShell title="User Detail">
        <div className="space-y-4">
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-48 w-full" />
        </div>
      </AppShell>
    );
  }

  if (error || !detail) {
    return (
      <AppShell title="User Detail">
        <ErrorState message={error ?? "User not found"} onRetry={load} />
      </AppShell>
    );
  }

  const { profile, balance, vip, recentLedger, reportsAgainst } = detail;

  return (
    <AppShell title="User Detail">
      <Button variant="ghost" size="sm" onClick={() => navigate("/users")} className="mb-4">
        ← Back to Users
      </Button>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <Card className="p-5 lg:col-span-1">
          <div className="flex flex-col items-center text-center">
            <Avatar src={profile.avatar_url} className="h-20 w-20 rounded-full object-cover" />
            <h2 className="mt-3 text-lg font-bold text-ink">{profile.username}</h2>
            <p className="mt-1 font-mono text-xs text-ink-faint">ID: {profile.user_id}</p>

            <div className="mt-3 flex gap-1.5">
              {profile.is_verified && <Badge tone="gold">Verified</Badge>}
              {profile.is_banned && <Badge tone="danger">Banned</Badge>}
              {vip && <Badge tone="gold">{vip.tier.toUpperCase()} VIP</Badge>}
            </div>

            <div className="mt-4 grid w-full grid-cols-2 gap-2 text-left">
              <div className="rounded-lg bg-surface-raised p-3">
                <p className="text-[10px] uppercase text-ink-faint">Balance</p>
                <p className="text-lg font-bold text-gold">{(balance ?? 0).toLocaleString()}</p>
              </div>
              <div className="rounded-lg bg-surface-raised p-3">
                <p className="text-[10px] uppercase text-ink-faint">Joined</p>
                <p className="text-sm font-semibold text-ink">{new Date(profile.created_at).toLocaleDateString()}</p>
              </div>
            </div>

            {vip && <p className="mt-2 text-xs text-ink-faint">VIP expires {new Date(vip.expires_at).toLocaleDateString()}</p>}

            <div className="mt-4 flex w-full flex-col gap-2">
              <Button size="sm" variant="secondary" onClick={openEditForm}>
                Edit Profile
              </Button>
              <Button size="sm" variant="secondary" onClick={toggleVerify}>
                {profile.is_verified ? "Remove Verification" : "Verify User"}
              </Button>
              {profile.is_banned ? (
                <Button size="sm" variant="secondary" onClick={() => setPendingStepUp("unban")}>
                  Unban User
                </Button>
              ) : (
                <Button size="sm" variant="danger" onClick={openBanForm}>
                  Ban User
                </Button>
              )}
              <Button size="sm" variant="ghost" onClick={openHistory}>
                Ban History
              </Button>
              <Link to={`/chat-moderation?userId=${profile.id}`}>
                <Button size="sm" variant="ghost" className="w-full">
                  View Chat Activity
                </Button>
              </Link>
            </div>
          </div>
        </Card>

        <div className="space-y-4 lg:col-span-2">
          <Card className="p-5">
            <h3 className="mb-3 text-sm font-semibold text-ink">Recent Transactions</h3>
            {recentLedger.length === 0 ? (
              <p className="text-sm text-ink-faint">No ledger activity yet.</p>
            ) : (
              <div className="space-y-2">
                {recentLedger.map((entry) => (
                  <div key={entry.id} className="flex items-center justify-between border-b border-surface-border pb-2 last:border-0">
                    <div className="flex items-center gap-2">
                      <Badge tone={reasonTone[entry.reason] ?? "neutral"}>{entry.reason}</Badge>
                      <span className="text-xs text-ink-faint">{new Date(entry.created_at).toLocaleString()}</span>
                    </div>
                    <span className={entry.amount >= 0 ? "font-semibold text-success" : "font-semibold text-danger"}>
                      {entry.amount >= 0 ? "+" : ""}
                      {entry.amount.toLocaleString()}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </Card>

          <Card className="p-5">
            <h3 className="mb-3 text-sm font-semibold text-ink">Reports Filed Against This User</h3>
            {reportsAgainst.length === 0 ? (
              <p className="text-sm text-ink-faint">No reports on record.</p>
            ) : (
              <div className="space-y-2">
                {reportsAgainst.map((report) => (
                  <div key={report.id} className="flex items-center justify-between border-b border-surface-border pb-2 last:border-0">
                    <div>
                      <p className="text-sm text-ink">{report.reason}</p>
                      <p className="text-xs text-ink-faint">{new Date(report.created_at).toLocaleString()}</p>
                    </div>
                    <Badge tone={report.status === "pending" ? "warn" : report.status === "reviewed" ? "success" : "neutral"}>{report.status}</Badge>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>
      </div>

      {/* Ban form — collected before the step-up dialog, which then fires the actual API call. */}
      <Modal
        isOpen={isBanFormOpen}
        onClose={() => setIsBanFormOpen(false)}
        title={`Ban ${profile.username}`}
        footer={
          <>
            <Button variant="ghost" onClick={() => setIsBanFormOpen(false)}>
              Cancel
            </Button>
            <Button variant="danger" onClick={submitBanForm}>
              Continue
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          <Select label="Ban type" value={banType} onChange={(e) => setBanType(e.target.value as "account" | "device")}>
            <option value="account">Account</option>
            <option value="device">Device</option>
          </Select>
          {banType === "device" && (
            <Input
              label="Device fingerprint"
              placeholder="Device identifier"
              value={deviceFingerprint}
              onChange={(e) => setDeviceFingerprint(e.target.value)}
              hint="No recent device identifier is surfaced for this user yet — enter it manually."
            />
          )}
          <Select label="Duration" value={duration} onChange={(e) => setDuration(e.target.value as Duration)}>
            <option value="1">1 day</option>
            <option value="7">7 days</option>
            <option value="permanent">Permanent</option>
            <option value="custom">Custom</option>
          </Select>
          {duration === "custom" && (
            <Input
              label="Custom duration (days)"
              type="number"
              min={1}
              value={customDays}
              onChange={(e) => setCustomDays(e.target.value)}
            />
          )}
          <Textarea label="Reason (optional)" value={banReason} onChange={(e) => setBanReason(e.target.value)} rows={2} />
        </div>
      </Modal>

      <Modal isOpen={isHistoryOpen} onClose={() => setIsHistoryOpen(false)} title={`Ban history — ${profile.username}`}>
        {isLoadingHistory ? (
          <Skeleton className="h-24 w-full" />
        ) : !banHistory || banHistory.length === 0 ? (
          <p className="text-sm text-ink-faint">No ban history on record.</p>
        ) : (
          <div className="max-h-96 space-y-2 overflow-y-auto">
            {banHistory.map((ban) => (
              <div key={ban.id} className="rounded-lg border border-surface-border p-3">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-medium text-ink">{formatBanRow(ban)}</p>
                  <Badge tone={ban.unbanned_at ? "neutral" : "danger"}>{ban.unbanned_at ? "Lifted" : "Active"}</Badge>
                </div>
                <p className="mt-1 text-xs text-ink-faint">
                  Banned {new Date(ban.banned_at).toLocaleString()} · {ban.expires_at ? `Expires ${new Date(ban.expires_at).toLocaleString()}` : "Permanent"}
                </p>
                {ban.unbanned_at && <p className="mt-0.5 text-xs text-ink-faint">Unbanned {new Date(ban.unbanned_at).toLocaleString()}</p>}
              </div>
            ))}
          </div>
        )}
      </Modal>

      <Modal
        isOpen={isEditOpen}
        onClose={() => setIsEditOpen(false)}
        title={`Edit ${profile.username}`}
        footer={
          <>
            <Button variant="ghost" onClick={() => setIsEditOpen(false)}>
              Cancel
            </Button>
            <Button onClick={saveEdit} isLoading={isSavingEdit}>
              Save
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          <Input label="Username" value={editForm.username} onChange={(e) => setEditForm((f) => ({ ...f, username: e.target.value }))} />
          <Input label="Avatar URL" value={editForm.avatarUrl} onChange={(e) => setEditForm((f) => ({ ...f, avatarUrl: e.target.value }))} />
          <Select label="Gender" value={editForm.gender} onChange={(e) => setEditForm((f) => ({ ...f, gender: e.target.value }))}>
            <option value="">Unchanged</option>
            <option value="male">Male</option>
            <option value="female">Female</option>
          </Select>
          <Input label="Country" value={editForm.country} onChange={(e) => setEditForm((f) => ({ ...f, country: e.target.value }))} />
          <Textarea label="Signature" value={editForm.signature} onChange={(e) => setEditForm((f) => ({ ...f, signature: e.target.value }))} rows={2} />
          <div className="flex items-end gap-3">
            <Input
              label="Username color (Profile screen)"
              type="color"
              value={editForm.usernameColor || "#1A1A1A"}
              onChange={(e) => setEditForm((f) => ({ ...f, usernameColor: e.target.value }))}
            />
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setEditForm((f) => ({ ...f, usernameColor: "" }))}
            >
              Reset to default
            </Button>
          </div>
          <Input
            label="Admin badge icon URL (Profile screen)"
            value={editForm.adminBadgeIconUrl}
            onChange={(e) => setEditForm((f) => ({ ...f, adminBadgeIconUrl: e.target.value }))}
            placeholder="https://… (leave blank for no badge)"
          />
        </div>
      </Modal>

      <StepUpDialog
        isOpen={!!pendingStepUp}
        onClose={() => setPendingStepUp(null)}
        onVerified={runStepUpAction}
        title={`${pendingStepUp === "ban" ? "Ban" : "Unban"} ${profile.username}?`}
        description="This immediately affects their access to the app. Re-enter your password to confirm."
      />
    </AppShell>
  );
}
